import { type Scene } from '../scene/Scene';
import { type EngineContext } from './EngineContext';

/**
 * The fixed slots a frame is divided into, in the order they run.
 *
 * Values are spaced so a custom system can be dropped between two built-in stages without renumbering
 * anything. `Physics` and `Animation` are reserved: nothing occupies them yet, but their position in
 * the order is already decided, so adding them later cannot reshuffle existing behavior.
 */
export const SystemStage = {
  /** Drain pointer and keyboard events, update pointer state, run hit-testing. */
  Input: 100,
  /** Run every behavior's `onUpdate`, in attach order. */
  Behaviors: 200,
  /** Broadphase over colliders, emit enter and exit events. */
  Collision: 300,
  /** Reserved. */
  Physics: 400,
  /** Reserved. */
  Animation: 500,
  /** Push component data into the renderer and present the frame. */
  Render: 600,
  /** Introspection snapshot and debug draw, only when enabled. */
  Debug: 700,
} as const;

/** One of the {@link SystemStage} slots. */
export type SystemStage = (typeof SystemStage)[keyof typeof SystemStage];

/**
 * Engine logic that has to see every entity of a kind, rather than live on one of them.
 *
 * The dividing line against a behavior is ownership: a behavior is what one entity does, a system is
 * what the engine does to all of them. Collision, render sync and input hit-testing are systems
 * because each needs the whole scene; following the pointer is a behavior because it needs one car.
 */
export interface System {
  /** Which slot in the frame this system runs in. */
  readonly stage: SystemStage;

  /** Called once when the system is registered, before its first update. */
  onRegister?(context: EngineContext): void;

  /** Called once per frame with the active scene, in {@link System.stage} order. */
  onUpdate(scene: Scene, deltaSeconds: number): void;

  /** Called when the system is removed or the engine is disposed. Release anything acquired here. */
  onUnregister?(): void;
}
